import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * Cross-link bar — drives buyers between Energetics / Mounts / Capabilities /
 * Quality without forcing them back to the nav. Each page passes the set of
 * routes it wants to surface; the current route is automatically excluded.
 *
 * Also a strong on-site SEO signal — internal cross-linking distributes link
 * equity and lifts on-site time (a ranking input).
 */
const ALL = {
  "/energetics": {
    title: "Energetics",
    blurb: "Countermeasure cartridges, impulse cartridges, pyrotechnics, and energetic devices.",
  },
  "/energetics/impulse-cartridges": {
    title: "Impulse Cartridges",
    blurb: "CCU-136A/A, CCU-145/A, BBU-36/B and BBU-48A/B cartridges for countermeasure dispensing.",
  },
  "/energetics/cad-pad": {
    title: "CAD/PAD",
    blurb: "Cartridge- and propellant-actuated devices for release, ejection and actuation.",
  },
  "/energetics/initiators-squibs-eeds": {
    title: "Initiators, Squibs & EEDs",
    blurb: "Electric initiators and electro-explosive devices, sealed and tested in-house.",
  },
  "/insights/how-to-choose-energetics-manufacturer": {
    title: "Buyer Guide",
    blurb: "How to choose an energetics manufacturer: six things to check.",
  },
  "/insights": {
    title: "Energetics Insights",
    blurb: "Guides to energetics manufacturing, CAD/PAD and initiator sealing.",
  },
  "/mounts-mechanical": {
    title: "Mounts & Mechanical",
    blurb: "M205, M192, ATEM, barrels, and custom mechanical solutions.",
  },
  "/capabilities": {
    title: "Capabilities",
    blurb: "Engineering, manufacturing, assembly, testing, and production services.",
  },
  "/capabilities/aerospace-machining": {
    title: "Aerospace Machining",
    blurb: "Specialized, low-volume CNC machining and build-to-print assemblies.",
  },
  "/quality-testing": {
    title: "Quality & Testing",
    blurb: "ISO 9001, MIL-STD qualification, metrology, inspection, and testing.",
  },
  "/about": {
    title: "About Valor",
    blurb: "U.S. defense manufacturer · Grand Junction, Colorado.",
  },
};

// Energetics-first default order; mounts stay reachable from the main navigation.
const DEFAULT_ORDER = [
  "/energetics",
  "/energetics/impulse-cartridges",
  "/energetics/cad-pad",
  "/energetics/initiators-squibs-eeds",
  "/insights/how-to-choose-energetics-manufacturer",
  "/quality-testing",
  "/capabilities",
  "/insights",
  "/about",
];

export default function RelatedPages({ current, items, eyebrow = "Continue exploring" }) {
  const routes = (items && items.length ? items : DEFAULT_ORDER).filter(
    (p) => p !== current
  );
  if (!routes.length) return null;
  return (
    <section
      className="bg-bone border-y border-line py-16 md:py-20"
      data-testid="related-pages"
    >
      <div className="max-w-[1300px] mx-auto px-6 md:px-10">
        <div className="font-mono text-[14px] tracking-[0.32em] uppercase text-signal mb-7 flex items-center gap-3">
          <span className="w-6 h-px bg-signal" />
          {eyebrow}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
          {routes.slice(0, 6).map((path) => {
            const meta = ALL[path];
            if (!meta) return null;
            return (
              <Link
                key={path}
                to={path}
                data-testid={`related-${path.replace(/\W/g, "-")}`}
                className="group bg-white p-7 md:p-8 transition-colors hover:bg-navy hover:text-white"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="font-display font-bold text-xl md:text-2xl text-navy group-hover:text-white tracking-tight transition-colors">
                    {meta.title}
                  </div>
                  <ArrowUpRight
                    className="w-5 h-5 text-signal mt-0.5 transition-transform group-hover:rotate-45"
                    strokeWidth={2}
                  />
                </div>
                <p className="text-steel group-hover:text-white/80 text-[15px] leading-[1.6] transition-colors">
                  {meta.blurb}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
